import React, { useState, useRef, useEffect } from 'react';
import { TextField, TextFieldProps } from '@mui/material';
import MacroSearchModal from './MacroSearchModal';

interface MacroTextFieldProps extends Omit<TextFieldProps, 'onChange'> {
  onChange: (value: string) => void;
  value: string;
}

const MacroTextField: React.FC<MacroTextFieldProps> = ({
  onChange,
  value,
  multiline,
  ...props
}) => {
  const [showMacroModal, setShowMacroModal] = useState(false);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const inputRef = useRef<HTMLInputElement | HTMLTextAreaElement>(null);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const newValue = e.target.value;
    onChange(newValue);

    // Check if the user just typed "/m" or "/M" (case insensitive)
    if (newValue.match(/\/[mM]$/)) {
      setAnchorEl(e.target);
      setShowMacroModal(true);
    }
  };

  const handleMacroSelect = (macro: { name: string; description: string; content: string }) => {
    // Replace "/m" or "/M" with the macro's content
    const newValue = value.replace(/\/[mM]$/, macro.content);
    onChange(newValue);
    setShowMacroModal(false);
    setAnchorEl(null);
    
    // Focus back to the input after macro selection
    setTimeout(() => {
      if (inputRef.current) {
        inputRef.current.focus();
      }
    }, 100);
  };

  // Tab-to-next-prompt feature and close macro modal on Escape
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    if (e.key === 'Escape' && showMacroModal) {
      e.preventDefault();
      setShowMacroModal(false);
      return;
    }
    
    if (e.key === 'Tab') {
      const el = inputRef.current;
      if (!el) return;
      const cursorPos = el.selectionStart ?? 0;
      // Regex to find [prompt] or [prompt:any-text] placeholders
      const regex = /\[prompt(?::.*?)?\]/gi;
      let match;
      let found = false;
      while ((match = regex.exec(value)) !== null) {
        if (match.index > cursorPos) {
          // Found the next prompt after the cursor
          e.preventDefault();
          // Select the prompt
          el.focus();
          // @ts-ignore
          if (typeof el.setSelectionRange === 'function') {
            el.setSelectionRange(match.index, match.index + match[0].length);
          }
          found = true;
          break;
        }
      }
      // If none found after cursor, optionally loop to first prompt
      if (!found) {
        // Optionally, loop to the first prompt
        regex.lastIndex = 0;
        match = regex.exec(value);
        if (match) {
          e.preventDefault();
          el.focus();
          // @ts-ignore
          if (typeof el.setSelectionRange === 'function') {
            el.setSelectionRange(match.index, match.index + match[0].length);
          }
        }
      }
    }
  };

  return (
    <>
      <TextField
        {...props}
        value={value}
        onChange={handleChange}
        inputRef={inputRef}
        multiline={multiline}
        inputProps={{
          onKeyDown: handleKeyDown,
        }}
        sx={{
          ...props.sx,
          width: '100%',
          '& .MuiInputBase-root': {
            ...(multiline && {
              resize: 'vertical',
              '& textarea': {
                resize: 'vertical',
                minHeight: '75px',
                '&::-webkit-resizer': {
                  borderWidth: '8px',
                  borderStyle: 'solid',
                  borderColor: 'transparent #666 #666 transparent',
                  boxShadow: '0 0 0 1px #666',
                }
              }
            })
          }
        }}
      />
      <MacroSearchModal
        open={showMacroModal}
        onClose={() => {
          setShowMacroModal(false);
          setAnchorEl(null);
        }}
        onSelect={handleMacroSelect}
        anchorEl={anchorEl}
      />
    </>
  );
};

export default MacroTextField; 